import { constants, dateTimeHelpers, domHelpers, errorHelpers, hooks } from '@approvalmax/utils';
import { forwardRef, ForwardRefExoticComponent, memo, MemoExoticComponent, RefAttributes, useRef } from 'react';

import { ClockIcon } from '../../icons';
import { Button } from '../Button/Button';
import { DigitalClock } from '../DigitalClock/DigitalClock';
import Divider from '../Divider/Divider';
import Dropdown from '../Dropdown/Dropdown';
import { Grid } from '../Grid/Grid';
import { TextField } from '../TextField/TextField';
import { HTMLTextFieldElement } from '../TextField/TextField.types';
import { Controller } from './components';
import { timePickerMaxHeight } from './TimePicker.constants';
import { useCallbackActions, useTextFieldValue, useTimeValue } from './TimePicker.hooks';
import { messages } from './TimePicker.messages';
import { ChildrenComponents, TimePickerProps } from './TimePicker.types';

/**
 * Time picker component with input field and DigitalClock selection
 */
export const TimePicker = memo(
    forwardRef((props, ref) => {
        const {
            placement = 'bottom-start',
            onOpen,
            initOpen,
            value,
            onChange,
            onFocus,
            onBlur,
            initFocus,
            clearable = true,
            noUtc,
            ampm,
            qa: qaProp,
            step,
            disabled,
            readOnly,
            ...restProps
        } = props;

        const dropdownRef = useRef<HTMLDivElement>(null);
        const activatorRef = useRef<HTMLDivElement>(null);
        const inputRef = useRef<HTMLTextFieldElement>(null);
        const composedInputRefs = hooks.useComposedRefs(ref, inputRef);

        if (typeof onChange !== 'function') {
            throw errorHelpers.notSupportedError(
                'TimePicker: Component does not support uncontrolled mode. `onChange` callback is required'
            );
        }

        const qa = domHelpers.generateDataQa(qaProp, 'time-picker');

        const { timeValue, changeTimePickerValue } = useTimeValue({
            value,
            onChange,
            noUtc,
            inputRef,
        });

        const {
            handleOpen,
            open,
            handleClick,
            handleKeyDown,
            focus,
            hover,
            handleMouseEnter,
            handleMouseLeave,
            toggleOpen,
            toggleFocus,
        } = useCallbackActions({
            onOpen,
            initOpen,
            initFocus,
        });

        const { handleFocus } = hooks.useOpenControl({
            inputRef,
            toggleFocus,
            toggleOpen,
            focus,
            open,
            initFocus,
            disabled,
            readOnly,
            onFocus,
        });

        const { handleBlur } = hooks.useCloseControl({
            activatorRef,
            dropdownRef,
            toggleFocus,
            toggleOpen,
            focus,
            open,
            onBlur,
        });

        const { textFieldValue, handleTextFieldChange } = useTextFieldValue({
            timeValue,
            changeTimePickerValue,
        });

        const isShowClearIcon = clearable && Boolean(textFieldValue) && hover;

        return (
            <Dropdown
                activator={
                    <TextField
                        {...restProps}
                        value={textFieldValue}
                        onKeyDown={handleKeyDown}
                        onChange={handleTextFieldChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        onClick={handleClick}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                        focus={focus}
                        clearable={isShowClearIcon}
                        type='time'
                        disabled={disabled}
                        readOnly={readOnly}
                        endIcon={isShowClearIcon ? undefined : <ClockIcon color='midnight100' pointer />}
                        qa={qa}
                        inputRef={composedInputRefs}
                        rootRef={activatorRef}
                    />
                }
                placement={placement}
                open={open}
                onOpen={handleOpen}
                maxHeight={0}
                width='max-content'
                isClicking={false}
                display='block'
                hasArrow={false}
                floatingPortalProps={constants.removeDropdownFocus.defaultFloatingPortalProps}
                floatingFocusManagerProps={constants.removeDropdownFocus.defaultFloatingFocusManagerProps}
                ref={dropdownRef}
            >
                <Grid padding='16' data-qa={domHelpers.generateDataQa(qa, 'popup')}>
                    <DigitalClock
                        height={timePickerMaxHeight}
                        ampm={typeof ampm === 'boolean' ? ampm : dateTimeHelpers.checkIs12HoursTimeFormat()}
                        qa={qa}
                        value={timeValue}
                        onChange={changeTimePickerValue}
                        step={step}
                    />

                    <Divider />

                    <Grid padding='12 0 0 0' justifyItems='end'>
                        <Button color='blue10' size='small' onClick={() => handleOpen(false)}>
                            {messages.ok}
                        </Button>
                    </Grid>
                </Grid>
            </Dropdown>
        );
    })
) as MemoExoticComponent<ForwardRefExoticComponent<TimePickerProps & RefAttributes<HTMLInputElement>>> &
    ChildrenComponents;

TimePicker.displayName = 'TimePicker';
TimePicker.Controller = Controller;
